import {CanvasRenderingTarget2D} from 'fancy-canvas';
import {
    customSeriesDefaultOptions,
    CustomSeriesOptions,
    CustomSeriesWhitespaceData,
    ICustomSeriesPaneRenderer,
    ICustomSeriesPaneView,
    PaneRendererCustomData,
    PriceToCoordinateConverter,
    UTCTimestamp,
} from 'lightweight-charts';

export interface ForecastBatchCandle {
    id: string;
    color: string;
    open: number;
    high: number;
    low: number;
    close: number;
    emphasized: boolean;
}

export interface ForecastSlotData {
    time: UTCTimestamp;
    color?: string;
    candles: ForecastBatchCandle[];
}

class ForecastBatchRenderer implements ICustomSeriesPaneRenderer {
    private data: PaneRendererCustomData<UTCTimestamp, ForecastSlotData> | null = null;

    update(data: PaneRendererCustomData<UTCTimestamp, ForecastSlotData>) {
        this.data = data;
    }

    draw(target: CanvasRenderingTarget2D, priceToCoordinate: PriceToCoordinateConverter): void {
        const data = this.data;
        if (!data?.visibleRange) return;

        target.useBitmapCoordinateSpace(scope => {
            const context = scope.context;
            const from = Math.max(0, data.visibleRange!.from);
            const to = Math.min(data.bars.length, data.visibleRange!.to);
            const slotWidth = Math.max(3, data.barSpacing * data.conflationFactor * 0.72);

            context.save();
            for (let index = from; index < to; index += 1) {
                const item = data.bars[index];
                const candles = item.originalData.candles;
                if (!candles.length) continue;
                const subWidth = slotWidth / candles.length;

                candles.forEach((candle, candleIndex) => {
                    const open = priceToCoordinate(candle.open);
                    const high = priceToCoordinate(candle.high);
                    const low = priceToCoordinate(candle.low);
                    const close = priceToCoordinate(candle.close);
                    if (open == null || high == null || low == null || close == null) return;

                    const centerX = item.x - slotWidth / 2 + subWidth * (candleIndex + 0.5);
                    const x = centerX * scope.horizontalPixelRatio;
                    const bodyWidth = Math.max(1, subWidth * 0.68 * scope.horizontalPixelRatio);
                    const openY = open * scope.verticalPixelRatio;
                    const closeY = close * scope.verticalPixelRatio;
                    const highY = high * scope.verticalPixelRatio;
                    const lowY = low * scope.verticalPixelRatio;
                    const bodyTop = Math.min(openY, closeY);
                    const bodyHeight = Math.max(1, Math.abs(closeY - openY));

                    context.globalAlpha = candle.emphasized ? 0.95 : 0.58;
                    context.strokeStyle = candle.color;
                    context.fillStyle = candle.color;
                    context.lineWidth = Math.max(1, scope.horizontalPixelRatio);
                    // 预测K线虚线样式：影线与实体边框用虚线，与实盘K线区分（2026-08-01）
                    const dashLength = 3 * scope.horizontalPixelRatio;
                    context.setLineDash([dashLength, dashLength]);
                    context.beginPath();
                    context.moveTo(x, highY);
                    context.lineTo(x, lowY);
                    context.stroke();
                    context.globalAlpha = candle.emphasized ? 0.38 : 0.18;
                    context.fillRect(x - bodyWidth / 2, bodyTop, bodyWidth, bodyHeight);
                    context.globalAlpha = candle.emphasized ? 1 : 0.72;
                    context.strokeRect(x - bodyWidth / 2, bodyTop, bodyWidth, bodyHeight);
                    context.setLineDash([]);
                });
            }
            context.restore();
        });
    }
}

export class ForecastBatchSeries implements ICustomSeriesPaneView<UTCTimestamp, ForecastSlotData, CustomSeriesOptions> {
    private readonly paneRenderer = new ForecastBatchRenderer();

    renderer(): ICustomSeriesPaneRenderer {
        return this.paneRenderer;
    }

    update(
        data: PaneRendererCustomData<UTCTimestamp, ForecastSlotData>,
        _seriesOptions: CustomSeriesOptions,
    ): void {
        this.paneRenderer.update(data);
    }

    priceValueBuilder(data: ForecastSlotData): number[] {
        if (!data.candles.length) return [];
        return [
            Math.min(...data.candles.map(candle => candle.low)),
            Math.max(...data.candles.map(candle => candle.high)),
            data.candles[data.candles.length - 1].close,
        ];
    }

    isWhitespace(
        data: ForecastSlotData | CustomSeriesWhitespaceData<UTCTimestamp>,
    ): data is CustomSeriesWhitespaceData<UTCTimestamp> {
        return !('candles' in data) || data.candles.length === 0;
    }

    defaultOptions(): CustomSeriesOptions {
        return {
            ...customSeriesDefaultOptions,
            color: '#13c2c2',
            priceLineVisible: false,
            lastValueVisible: false,
        };
    }
}
